import React, { useState, useRef, useEffect, useMemo } from "react";
import debounce from "lodash.debounce";
import { cn } from "tailwind-variants";
import { pluralize } from "common/utils/format/word";
import { isMobile } from "common/utils/browser";
import addUrlQueryParams from "common/utils/url";
import Loader from "common/vue/components/Loader.react.vue";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import { track } from "jiffy-analytics";
import UseSvg from "common/vue/components/UseSvg.react.vue";
import ArtworkHistoryModalEmptyState from "./ArtworkHistoryModalEmptyState.vue";
import ArtworkHistoryGridItem from "./ArtworkHistoryGridItem.vue";
import ArtworkHistoryModalFooter from "./ArtworkHistoryModalFooter.vue";
import { isArtworkWithError } from "./utils";
import { useDtfStore } from "./store";
import { useSpanglesStore } from "./spangles-module";
import ArtworkHistoryArtworksToggle from "./ArtworkHistoryArtworksToggle.vue";
import ArtworkHistoryNotification from "./ArtworkHistoryNotification.vue";
import "./ArtworkHistoryModal.scss";
import { renderList } from "common/vue/render-list";

export default function ArtworkHistoryModal({
  index = 0,
  singleSelectionMode = false,
  reorderEnabled = false,
  isMade = false,
  onBuyAgain,
  ...restProps
}) {
  const dtfStore = useDtfStore();
  const spanglesStore = useSpanglesStore();
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [isLoadingCta, setIsLoadingCta] = useState(false);
  const [sortOrder, setSortOrder] = useState("desc");
  const [artworksType, setArtworksType] = useState("uploaded");
  const [currentType, setCurrentType] = useState("true");
  const [search, setSearch] = useState("");
  const [selectedArtworks, setSelectedArtworks] = useState([]);
  const [apiLoading, setApiLoading] = useState(false);
  const [isLazyLoading, setIsLazyLoading] = useState(false);

  const modalRef = useRef(null);
  const sortOrderSelectRef = useRef(null);
  const searchRef = useRef("");
  const prevSearchRef = useRef("");

  // Computed properties
  function artworkHistory() {
    return dtfStore.artworkHistory;
  }

  function artworkHistoryPagination() {
    return dtfStore.artworkHistoryPagination;
  }

  function catalogNumber() {
    return dtfStore.catalogNumber;
  }

  function pdpType() {
    return dtfStore.pdpType;
  }

  function sticker() {
    return dtfStore.sticker;
  }

  function isPuff() {
    return dtfStore.isPuff;
  }

  function isSpangle() {
    return dtfStore.isSpangle;
  }

  function isStickerSample() {
    return dtfStore.isStickerSample;
  }

  function stickerQrScanned() {
    return dtfStore.stickerQrScanned;
  }

  function artworks() {
    return dtfStore.artworks;
  }

  function isGoldFoil() {
    return dtfStore.isGoldFoil;
  }

  function puffMaxImageCount() {
    return dtfStore.puffMaxImageCount;
  }

  function printAsIs() {
    return dtfStore.printAsIs;
  }

  function businessLine() {
    return dtfStore.businessLine;
  }

  function spanglesMaxImageCount() {
    return spanglesStore.spanglesMaxImageCount;
  }

  function mapArtworkItem({
    id = null,
    fileName = "",
    originalMime = "",
    enhancementTier = "",
    uploadedOn = "",
    purchasedDate = "",
    ordered = {},
    original = {},
    useFootLength,
    dtfPrintId
  }) {
    return {
      id,
      dtfPrintId,
      name: fileName,
      uploadedOn,
      purchasedDate,
      originalMime,
      enhancementTier,
      url: ordered.imageUrl,
      previewUrl: getPreviewUrl(original, ordered),
      downloadUrl: getDownloadUrl(original, ordered),
      spangled: getSpangledState(ordered),
      spangledImageUrl: ordered?.spangledImageUrl,
      cutlineUrl: ordered?.cutlineUrl,
      originalImageUrl: original.imageUrl,
      originalMimeType: original.mimeType,
      cmykPreviewImageNoBackgroundUrl: ordered?.cmykPreviewImageNoBackgroundUrl,
      cmykPreviewImageWithBackgroundUrl: ordered?.cmykPreviewImageWithBackgroundUrl,
      processTimeStamp: Date.now(),
      goildFoilPreviewAttached: isGoldFoil() && currentType === "true",
      enhancedNoBgUrl: ordered.noBgUrl,
      enhancedWithBgUrl: ordered.withBgUrl,
      enhancementCompleted: true,
      originalDpi: ordered.dpi,
      enhancedDpi: ordered.dpi,
      sliderPreviewUrl: ordered.noBgNoTrimUrl,
      canRemoveBg: ordered.canRemoveBg || false,
      useFootLength: useFootLength || false,
      metadata: {
        ...ordered.metadata,
        dpi: ordered.dpi,
        width: ordered.widthPx,
        height: ordered.heightPx,
        widthInch: ordered.width,
        heightInch: ordered.height,
        maxSheetHeight: ordered.maxSheetHeight,
        maxSheetWidth: ordered.maxSheetWidth,
        aspectRatio: ordered?.enhanceImage
          ? (
              parseFloat(ordered?.enhancedMetadata?.width) /
              parseFloat(ordered?.enhancedMetadata?.height)
            ).toPrecision(6)
          : (
              parseFloat(ordered?.originalMetadata?.width) /
              parseFloat(ordered?.originalMetadata?.height)
            ).toPrecision(6),
        lengthFoot: ordered.lengthFoot,
        removeBackground: ordered.removeBackground
      },
      originalMetadata: ordered?.originalMetadata,
      enhancedMetadata: ordered?.enhancedMetadata,
      withBgMetadata: ordered?.withBgMetadata,
      enhanceImage: ordered?.enhanceImage,
      previewMetadata: printAsIs()
        ? {
            heightInch: ordered.height
          }
        : {},
      enhancedOriginalDpi: ordered?.enhancedOriginalDpi,
      originalWidth: defineOriginalWidth(ordered),
      originalHeight: defineOriginalHeight(ordered),
      sizeBasedDpi: ordered?.enhanceImage
        ? ordered?.enhancedOriginalDpi
        : ordered?.originalMetadata?.dpi
    };
  }

  const formattedArtworks = useMemo(
    () => artworkHistory().map(mapArtworkItem),
    [currentType, dtfStore.artworkHistory]
  );

  function selectedArtworkCountMessage() {
    if (isMaxNumberOfArtworksSelected()) {
      return `Maximum ${pluralize(selectedArtworks.length, "image", "s", true)} can be selected.`;
    }
    return `${selectedArtworks.length} Selected`;
  }

  function isMaxNumberOfArtworksSelected() {
    return (
      (isPuff() && selectedArtworks.length === puffMaxImageCount()) ||
      (isSpangle() && selectedArtworks.length === spanglesMaxImageCount()) ||
      (isStickerSample() && selectedArtworks.length >= 1)
    );
  }

  function buttonText() {
    if (isSpangle() && currentType === "false") {
      return "Spangle It";
    }
    if (reorderEnabled && artworksType === "purchased") {
      return "Add to Cart";
    }
    return `Select ${pluralize(selectedArtworks.length, "Design", "s", true)}`;
  }

  function withError() {
    return isArtworkWithError(artworks()[0]);
  }

  function isPdp() {
    return pdpType() !== "non-pdp";
  }

  // Store action wrappers
  function setShowArtworkHistoryModal(...args) {
    return dtfStore.setShowArtworkHistoryModal(...args);
  }

  function setArtworkHistory(...args) {
    return dtfStore.setArtworkHistory(...args);
  }

  function setArtworkHistoryPagination(...args) {
    return dtfStore.setArtworkHistoryPagination(...args);
  }

  function removeArtwork(...args) {
    return dtfStore.removeArtwork(...args);
  }

  function setSpangleVariations(...args) {
    return spanglesStore.setSpangleVariations(...args);
  }

  function getArtworkHistory(...args) {
    return dtfStore.getArtworkHistory(...args);
  }

  function submitPrintReorder(...args) {
    return dtfStore.submitPrintReorder(...args);
  }

  function buyArtworksAgain(...args) {
    return dtfStore.buyArtworksAgain(...args);
  }

  function downloadAndTriggerArtworkUploadFlow(...args) {
    return dtfStore.downloadAndTriggerArtworkUploadFlow(...args);
  }

  // Methods
  function selectArtworkHistory(type) {
    setCurrentType(type);
  }

  async function updateArtworksType(type) {
    setArtworksType(type);
    setArtworkHistoryPagination({
      page: 1
    });
    clearSelection();
    setArtworkHistory([]);
    await loadArtworkHistory();
  }

  async function onShow() {
    showModal();
    setArtworkHistoryPagination({
      perPage: isMobile() ? 20 : 36
    });
    loadArtworkHistory();
  }

  function onClose(source) {
    if (source) {
      sendArtworkHistoryEvent("previous_purchase_modal_closed", {
        source
      });
    }
    setArtworkHistory([]);
    setArtworkHistoryPagination({
      page: 1
    });
    $(modalRef.current).modal("hide");
  }

  function sendArtworkHistoryEvent(event, data) {
    submitDtfEvent(event, {
      page: "pdp",
      business_line: businessLine(),
      pdp_type: pdpType(),
      catalog_number: catalogNumber(),
      ...data
    });
  }

  function showModal() {
    const $modal = $(modalRef.current);
    $modal.modal("show").on("hidden.bs.modal", () => {
      onClose();
      setShowArtworkHistoryModal(false);
      document.body.classList.remove("dtf-artwork-history-open");
    });
    document.body.classList.add("dtf-artwork-history-open");
  }

  function defineOriginalWidth(ordered) {
    if (ordered.enhanceImage) {
      return ordered.removeBackground
        ? ordered.enhancedMetadata?.widthInch
        : ordered.withBgMetadata?.widthInch;
    }
    return ordered.originalMetadata?.widthInch;
  }

  function defineOriginalHeight(ordered) {
    if (ordered.enhanceImage) {
      return ordered.removeBackground
        ? ordered.enhancedMetadata?.heightInch
        : ordered.withBgMetadata?.heightInch;
    }
    return ordered.originalMetadata?.heightInch;
  }

  function getPreviewUrl(original, ordered) {
    if (isGoldFoil() && currentType === "true") return ordered.goldFoilImage;
    const imageUrl = getDownloadUrl(original, ordered);
    return addUrlQueryParams(imageUrl, {
      w: "150",
      q: "70",
      dpr: "2"
    });
  }

  function getDownloadUrl(original, ordered) {
    if (currentType === "false") {
      return original.imageUrl;
    }
    if (isSpangle() && ordered.spangledImageUrl) {
      return ordered.spangledImageUrl;
    }
    if (sticker() && ordered.cuttingUrl) {
      return ordered.cutlineUrl;
    }
    return original.imageUrl;
  }

  function getSpangledState(ordered) {
    return isSpangle() && ordered.spangled && currentType === "true";
  }

  async function buyPrintReorder() {
    setIsLoadingCta(true);
    await submitPrintReorder(selectedArtworks);
    onClose("buy_again_button");
    setIsLoadingCta(false);
  }

  async function buyAgain() {
    setArtworkHistoryPagination({
      page: 1
    });
    await fetchSelectedArtworks();
    const freshFormatted = artworkHistory().map(mapArtworkItem);
    setSelectedArtworks(freshFormatted);
    if (withError()) {
      removeArtwork(artworks()[0].id);
    }
    if (isSpangle()) setSpangleVariations([]);
    if (sticker()) {
      downloadAndTriggerArtworkUploadFlow(freshFormatted);
    } else if (isPdp()) {
      buyArtworksAgain(freshFormatted, index);
    } else if (onBuyAgain) {
      onBuyAgain(freshFormatted);
    }
    sendArtworkHistoryEvent("previous_purchase_buy_again_clicked", {
      artworks_ids: freshFormatted.map(artwork => artwork.id),
      artworks_count: freshFormatted.length
    });
    onClose("buy_again_button");
  }

  function clearLoading() {
    setApiLoading(false);
    setIsLazyLoading(false);
  }

  async function fetchSelectedArtworks() {
    setApiLoading(true);
    const props = {
      search,
      sortOrder,
      artworkIds: selectedArtworks.map(artwork => artwork.id),
      currentType,
      apiType: "purchased"
    };
    await getArtworkHistory(props);
    clearLoading();
  }

  async function loadArtworkHistory() {
    setApiLoading(true);
    const props = {
      artworksType,
      search,
      sortOrder,
      artworkIds: [],
      currentType,
      apiType: "list"
    };
    await getArtworkHistory(props);
    clearLoading();
  }

  function onToggleSearch() {
    if (isMobile()) {
      setIsSearchExpanded(!isSearchExpanded);
    }
  }

  function clearSelection() {
    setSelectedArtworks([]);
  }

  function checkArtworkSelection({ id, metadata }, artwork) {
    const selectedArtworkSize = `${metadata.widthInch}${metadata.heightInch}`;
    const artworkSize = `${artwork.metadata.widthInch}${artwork.metadata.heightInch}`;
    return id === artwork.id && selectedArtworkSize === artworkSize;
  }

  function isArtworkSelected(artwork) {
    return selectedArtworks.some(selectedArtwork =>
      checkArtworkSelection(selectedArtwork, artwork)
    );
  }

  function selectArtwork(artwork, artworkIndex) {
    if (singleSelectionMode) {
      setSelectedArtworks([formattedArtworks[artworkIndex]]);
      return;
    }
    if (isArtworkSelected(artwork)) {
      setSelectedArtworks(
        selectedArtworks.filter(selectedArtwork => !checkArtworkSelection(selectedArtwork, artwork))
      );
    } else {
      setSelectedArtworks([...selectedArtworks, formattedArtworks[artworkIndex]]);
    }
  }

  function scroll({ target }) {
    const { page, totalPages } = artworkHistoryPagination();
    const isBottom = Math.ceil(target.scrollTop) >= target.scrollHeight - target.offsetHeight;
    if (!isBottom || page >= totalPages) {
      return;
    }
    setIsLazyLoading(true);
    setArtworkHistoryPagination({
      page: page + 1
    });
    loadArtworkHistory();
    sendArtworkHistoryEvent("previous_purchase_lazy_loaded", {
      page_number: page + 1
    });
  }

  function onSearchImpl() {
    sendArtworkHistoryEvent("previous_purchase_search_triggered", {
      search_query: searchRef.current
    });
    setArtworkHistoryPagination({
      page: 1
    });
    clearSelection();
    loadArtworkHistory();
  }

  const debouncedOnSearch = useMemo(() => debounce(onSearchImpl, 600), []);

  function download(url, name) {
    fetch(url)
      .then(response => response.blob())
      .then(blob => {
        const $a = document.createElement("a");
        $a.href = URL.createObjectURL(blob);
        $a.download = name;
        document.body.appendChild($a);
        $a.click();
        document.body.removeChild($a);
        sendArtworkHistoryEvent("previous_purchase_image_downloaded", {
          file_name: name,
          image_url: url,
          file_size: blob.size,
          file_type: blob.type
        });
      });
  }

  function toggleSelect() {
    if (window.getComputedStyle(sortOrderSelectRef.current).display === "none") {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    }
  }

  function onSubmit() {
    if (reorderEnabled && artworksType === "purchased") {
      buyPrintReorder();
    } else {
      buyAgain();
    }
  }

  // Lifecycle
  useEffect(() => {
    setCurrentType("true");
    if (stickerQrScanned()) {
      setCurrentType("false");
    }
  }, []);

  useEffect(() => {
    onShow();
  }, []);

  useEffect(
    () => () => {
      $(modalRef.current).modal("hide");
      setShowArtworkHistoryModal(false);
    },
    []
  );

  // Watch sortOrder
  useEffect(() => {
    setArtworkHistoryPagination({
      page: 1
    });
    clearSelection();
    loadArtworkHistory();
  }, [sortOrder]);

  // Watch currentType
  useEffect(() => {
    setArtworkHistoryPagination({
      page: 1
    });
    setArtworkHistory([]);
    clearSelection();
    loadArtworkHistory();
    track("modal_with_previous_uploads_tab_selected", {
      current_type: currentType
    });
  }, [currentType]);

  // Watch search
  useEffect(() => {
    searchRef.current = search;
    if (!search && prevSearchRef.current) {
      setApiLoading(true);
    }
    prevSearchRef.current = search;
    debouncedOnSearch();
  }, [search]);

  return (
    <div
      {...restProps}
      className="modal artwork-history scope-165c6833"
      id="artwork-history"
      ref={modalRef}
      role="dialog"
      data-testid="modal"
    >
      <div className="artwork-history__modal-box modal-dialog scope-165c6833">
        <div
          className="artwork-history__modal-header scope-165c6833"
          role="heading"
          aria-label="Your previous purchases"
          tabIndex="0"
          data-testid="artwork-history-modal-header"
        >
          <span className="artwork-history__title scope-165c6833">My Library</span>
          <button
            className="artwork-history__modal-button-close scope-165c6833"
            type="button"
            onClick={() => {
              onClose("close_button");
            }}
            data-testid="artwork-history-modal-button-close"
          >
            <UseSvg className="scope-165c6833" icon="modal-close-big" data-testid="use-svg" />
          </button>
        </div>
        <div className="artwork-history__modal-body scope-165c6833">
          <div className="artwork-history__subheader scope-165c6833">
            {!isSearchExpanded ? (
              <div
                className="artwork-history__dropdown scope-165c6833"
                onClick={toggleSelect}
                data-testid="artwork-history-dropdown"
              >
                <span className="artwork-history__field-adornment scope-165c6833">
                  <UseSvg
                    className="artwork-history__field-adornment-icon scope-165c6833"
                    icon={`arrow-${sortOrder === "asc" ? "up" : "down"}`}
                    data-testid="artwork-history-field-adornment-icon"
                  />
                </span>
                <select
                  className="artwork-history__dropdown-select scope-165c6833"
                  ref={sortOrderSelectRef}
                  value={sortOrder}
                  onChange={e => {
                    setSortOrder(e.target.value);
                  }}
                  data-testid="artwork-history-dropdown-select"
                >
                  <option
                    className="scope-165c6833"
                    value="desc"
                    data-testid="artwork-history-modal-option"
                  >
                    Newest First
                  </option>
                  <option
                    className="scope-165c6833"
                    value="asc"
                    data-testid="artwork-history-modal-option-1"
                  >
                    Oldest First
                  </option>
                </select>
              </div>
            ) : null}
            {reorderEnabled && !isSearchExpanded ? (
              <ArtworkHistoryArtworksToggle
                className="artwork-history__toggle scope-165c6833"
                disabled={apiLoading}
                type={artworksType}
                onUpdateType={updateArtworksType}
                data-testid="artwork-history-toggle"
              />
            ) : null}
            <div
              className={cn(
                "artwork-history__input scope-165c6833",
                isSearchExpanded && "artwork-history__input--full-width"
              )}
            >
              {isSearchExpanded || !isMobile() ? (
                <input
                  className="artwork-history__input-field scope-165c6833"
                  value={search}
                  onInput={e => {
                    setSearch(e.target.value);
                  }}
                  placeholder="Search image names"
                  type="text"
                  data-testid="artwork-history-input-field"
                />
              ) : null}
              <span
                className="artwork-history__field-adornment scope-165c6833"
                onClick={onToggleSearch}
                data-testid="artwork-history-field-adornment"
              >
                <UseSvg
                  className="artwork-history__field-adornment-icon scope-165c6833"
                  icon="search"
                  data-testid="artwork-history-field-adornment-icon-1"
                />
              </span>
            </div>
            {isSearchExpanded ? (
              <button
                className="artwork-history__modal-button-close scope-165c6833"
                type="button"
                onClick={onToggleSearch}
                data-testid="artwork-history-modal-button-close-1"
              >
                <UseSvg className="scope-165c6833" icon="modal-close-big" data-testid="use-svg-1" />
              </button>
            ) : null}
          </div>
          {artworkHistory().length < 1 ? (
            <ArtworkHistoryModalEmptyState
              className="scope-165c6833"
              isUploadedState={reorderEnabled && artworksType === "uploaded"}
              isLoading={apiLoading}
              isMade={isMade}
              data-testid="artwork-history-modal-empty-state"
            />
          ) : (
            <div
              className="artwork-history__artworks-wrapper scope-165c6833"
              onScroll={scroll}
              data-testid="artwork-history-artworks-wrapper"
            >
              {reorderEnabled && artworksType === "purchased" ? (
                <ArtworkHistoryNotification
                  className="artwork-history__artworks-notification scope-165c6833"
                  data-testid="artwork-history-artworks-notification"
                >
                  You won't be charged for design services when reordering a design at the same
                  size.
                </ArtworkHistoryNotification>
              ) : null}
              <div className="artwork-history__grid scope-165c6833">
                {renderList(formattedArtworks, (artwork, artworkIndex) => (
                  <ArtworkHistoryGridItem
                    className="scope-165c6833"
                    key={artworkIndex}
                    artwork={artwork}
                    isSelected={isArtworkSelected(artwork)}
                    index={artworkIndex}
                    isShownTier={reorderEnabled && artworksType === "purchased"}
                    isShownCheckbox={!isMaxNumberOfArtworksSelected() || isArtworkSelected(artwork)}
                    onDownloadArtwork={download}
                    onSelectLibrary={selectArtwork}
                    data-testid="artwork-history-grid-item"
                  />
                ))}
                <div
                  className={cn(
                    "artwork-history__loading scope-165c6833",
                    isLazyLoading && "artwork-history__loading--visible"
                  )}
                >
                  <Loader
                    className={cn("scope-165c6833", "artwork-history__loading-icon")}
                    data-testid="artwork-history-loading-icon"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        {selectedArtworks.length > 0 ? (
          <ArtworkHistoryModalFooter
            className="scope-165c6833"
            count={selectedArtworkCountMessage()}
            ctaText={buttonText()}
            isLoadingCta={isLoadingCta}
            onClose={onClose}
            onCtaAction={onSubmit}
            data-testid="artwork-history-modal-footer"
          />
        ) : null}
      </div>
    </div>
  );
}
